import { useState, useRef, useEffect, ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

export type ExportStatusFilter = 'all' | 'completed';

interface ExportMenuProps {
  label: string;
  icon?: ReactNode;
  /** Tailwind classes for the trigger button (lets callers match their existing button styling). */
  className?: string;
  /** Fired with the chosen status scope when a menu item is selected. */
  onExport: (statusFilter: ExportStatusFilter) => void;
}

// CR-14: an export trigger that offers "All respondents" vs "Completes only".
const ExportMenu = ({ label, icon, className = '', onExport }: ExportMenuProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const choose = (statusFilter: ExportStatusFilter) => {
    setOpen(false);
    onExport(statusFilter);
  };

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(o => !o)} className={className} aria-haspopup="menu" aria-expanded={open}>
        {icon}
        {label}
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-44 rounded-lg border border-[#ECEEF1] bg-white shadow-lg py-1" role="menu">
          <button
            type="button"
            role="menuitem"
            onClick={() => choose('all')}
            className="w-full text-left px-3 py-2 text-[13px] text-[#374151] hover:bg-[#F5F7FA] transition-colors"
          >
            All respondents
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => choose('completed')}
            className="w-full text-left px-3 py-2 text-[13px] text-[#374151] hover:bg-[#F5F7FA] transition-colors"
          >
            Completes only
          </button>
        </div>
      )}
    </div>
  );
};

export default ExportMenu;
